import { Component, ChangeDetectionStrategy } from '@angular/core';

import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import {
  AuthService,
  ProfileStep1Data,
  ProfileStep2Data,
  ProfileStep3Data,
  ProfileStep4Data,
} from '../../services/auth.service';
import { CompanyService } from '../../services/company.service';
import { StorefrontService } from '../../services/storefront.service';
import { LoggerService } from '../../services/logger.service';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideArrowLeft,
  lucideArrowRight,
  lucideCheck,
  lucideTriangleAlert,
} from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmInputGroupImports } from '@spartan-ng/helm/input-group';
import { HlmItemImports } from '@spartan-ng/helm/item';
import { HlmNativeSelectImports } from '@spartan-ng/helm/native-select';
import { HlmProgressImports } from '@spartan-ng/helm/progress';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { HlmSwitchImports } from '@spartan-ng/helm/switch';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import { LogoComponent } from '../../components/logo/logo.component';
import { environment } from '../../../environments/environment';
import { Observable } from 'rxjs';
import { COUNTRIES } from '../../config/countries.config';

@Component({
  selector: 'app-onboarding',
  standalone: true,
  templateUrl: './onboarding.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ReactiveFormsModule,
    RouterModule,
    NgIcon,
    HlmAlertImports,
    HlmButtonImports,
    HlmCardImports,
    HlmFieldImports,
    HlmInputImports,
    HlmInputGroupImports,
    HlmItemImports,
    HlmNativeSelectImports,
    HlmProgressImports,
    HlmSpinnerImports,
    HlmSwitchImports,
    HlmTypographyImports,
    LogoComponent,
  ],
  providers: [
    provideIcons({ lucideArrowLeft, lucideArrowRight, lucideCheck, lucideTriangleAlert }),
  ],
})
export class OnboardingComponent {
  onboardingForm!: FormGroup;
  submitted = false;
  loading = false;
  error = '';
  currentStep = 0;
  steps = ['Kontakt', 'Rechnungsadresse', 'Geschäftsadresse', 'Gutschein-Shop'];
  completedSteps = 0; // Track how many steps have been completed
  /** A network member finishes with its business name instead of a shop (no Stripe). */
  isMember = false;
  networkName: string | null = null;
  hasNavigatedBack = false; // Track if user has used Zurück button

  // Available countries
  countries = COUNTRIES;

  get progressPercent(): number {
    return Math.round(((this.currentStep + 1) / this.steps.length) * 100);
  }

  /** Errors appear after a submit attempt, mirroring the previous behaviour. */
  isInvalid(controlName: string): boolean {
    return this.submitted && !!this.f[controlName]?.errors;
  }

  /** The control's first error as a sentence, server errors included. */
  errorFor(controlName: string): string {
    const errors = this.f[controlName]?.errors;
    if (!errors) return '';
    if (errors['serverError']) return errors['serverError'];
    if (errors['required']) return 'Dieses Feld ist erforderlich';
    if (errors['pattern']) {
      return controlName === 'suburl'
        ? 'Verwende nur Kleinbuchstaben, Zahlen und Bindestriche'
        : 'Bitte gib eine gültige Website-Adresse ein (z. B. www.beispiel.de)';
    }
    return 'Bitte prüfe deine Eingabe';
  }

  constructor(
    private formBuilder: FormBuilder,
    private authService: AuthService,
    private companyService: CompanyService,
    private storefrontService: StorefrontService,
    private router: Router,
    private logger: LoggerService,
  ) {
    // Check if user is logged in and has not completed profile
    if (!this.authService.isLoggedIn()) {
      this.router.navigate(['/login']);
      return;
    }

    if (this.authService.isProfileCompleted()) {
      this.router.navigate(['/']);
      return;
    }

    this.initializeForm();
    this.setupBusinessAddressValidation();
    this.loadProfileData();
  }

  initializeForm(): void {
    this.onboardingForm = this.formBuilder.group({
      // Contact
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      phone: ['', Validators.required],

      // Billing Address
      companyName: ['', Validators.required],
      billingStreet: ['', Validators.required],
      billingCity: ['', Validators.required],
      billingZip: ['', Validators.required],
      billingCountry: ['DE', Validators.required],

      // Business Address
      useBillingAsBusiness: [true],
      businessStreet: [''],
      businessCity: [''],
      businessZip: [''],
      businessCountry: ['DE'],

      // Gutschein-Shop
      shopName: ['', Validators.required],
      website: [
        '',
        [Validators.pattern(/^(https?:\/\/)?[\w\-]+(\.[\w\-]+)*\.[\w\-]{2,}([/#?].*)?$/)],
      ],
      suburl: ['', [Validators.required, Validators.pattern(/^[a-z0-9-]+$/)]],
    });
  }

  /** Swap the shop step for the member's business name; the IBAN is a dashboard task. */
  private switchToMemberOnboarding(networkName: string | null): void {
    this.isMember = true;
    this.networkName = networkName;
    this.steps = ['Kontakt', 'Rechnungsadresse', 'Geschäftsadresse', 'Dein Betrieb'];
    const f = this.onboardingForm.controls;
    f['suburl'].clearValidators(); // no shop: no storefront address
    f['suburl'].updateValueAndValidity({ emitEvent: false });
  }

  setupBusinessAddressValidation(): void {
    // Watch for changes to the useBillingAsBusiness checkbox
    this.onboardingForm.get('useBillingAsBusiness')?.valueChanges.subscribe((useBilling) => {
      const businessFields = ['businessStreet', 'businessCity', 'businessZip', 'businessCountry'];

      businessFields.forEach((fieldName) => {
        const control = this.onboardingForm.get(fieldName);
        if (control) {
          if (useBilling) {
            // If using billing as business, remove required validation
            control.clearValidators();
            control.setValidators([]);
          } else {
            // If not using billing as business, add required validation
            control.setValidators([Validators.required]);
          }
          control.updateValueAndValidity();
        }
      });
    });

    // Trigger initial validation setup
    const initialUseBilling = this.onboardingForm.get('useBillingAsBusiness')?.value;
    if (!initialUseBilling) {
      const businessFields = ['businessStreet', 'businessCity', 'businessZip', 'businessCountry'];
      businessFields.forEach((fieldName) => {
        const control = this.onboardingForm.get(fieldName);
        if (control) {
          control.setValidators([Validators.required]);
          control.updateValueAndValidity();
        }
      });
    }
  }

  loadProfileData(): void {
    this.authService.getOnboardingProfile().subscribe({
      next: (data) => {
        this.logger.log('📄 [OnboardingComponent] Loaded profile data:', data);

        // Populate form with existing data
        this.onboardingForm.patchValue({
          // Step 1 data
          firstName: data.first_name || '',
          lastName: data.last_name || '',
          phone: data.phone || '',

          // Step 2 data
          companyName: data.company_name || '',
          billingStreet: data.billing_street || '',
          billingCity: data.billing_city || '',
          billingZip: data.billing_zip || '',
          billingCountry: data.billing_country || 'DE',

          // Step 3 data - use the explicit use_billing_as_business field, or determine from address comparison
          useBillingAsBusiness:
            data.use_billing_as_business !== undefined
              ? data.use_billing_as_business
              : data.business_street === data.billing_street &&
                data.business_city === data.billing_city &&
                data.business_zip === data.billing_zip &&
                data.business_country === data.billing_country,
          businessStreet: data.business_street || '',
          businessCity: data.business_city || '',
          businessZip: data.business_zip || '',
          businessCountry: data.business_country || 'DE',

          // Step 4 data
          shopName: data.name || '',
          website: data.website ? data.website.replace(/^https?:\/\//, '') : '', // Remove protocol for display
          suburl: data.suburl || '',
        });

        if (data.account_type === 'member') {
          this.switchToMemberOnboarding(data.network_name ?? null);
        }

        // Set current step based on completed steps
        this.completedSteps = data.onboarding_step_completed;
        this.currentStep = Math.min(data.onboarding_step_completed, this.steps.length - 1);
        this.logger.log(
          `🎯 [OnboardingComponent] Resuming from step ${this.currentStep}, completed steps: ${this.completedSteps}`,
        );
      },
      error: (error) => {
        this.logger.error('❌ [OnboardingComponent] Failed to load profile data:', error);
        // Continue with empty form if loading fails
      },
    });
  }

  get f() {
    return this.onboardingForm.controls;
  }

  /**
   * Check if the current step has unsaved changes
   */
  hasUnsavedChanges(): boolean {
    const fields = this.getStepFieldNames(this.currentStep);
    return fields.some((fieldName) => {
      const control = this.onboardingForm.get(fieldName);
      return control ? control.dirty : false;
    });
  }

  /**
   * Mark current step fields as pristine after successful save
   */
  markCurrentStepAsPristine(): void {
    const fields = this.getStepFieldNames(this.currentStep);
    fields.forEach((fieldName) => {
      const control = this.onboardingForm.get(fieldName);
      if (control) {
        control.markAsPristine();
      }
    });
  }

  /**
   * Get the appropriate button text based on current state
   */
  getButtonText(): string {
    if (this.loading) {
      return '';
    }

    if (this.isLastStep()) {
      return 'Profil vervollständigen';
    }

    // Show "Speichern" only if user has navigated back and has unsaved changes
    if (this.hasNavigatedBack && this.hasUnsavedChanges()) {
      return 'Speichern';
    }

    return 'Weiter';
  }

  /**
   * Step helpers
   */
  getStepFieldNames(step: number): string[] {
    switch (step) {
      case 0:
        return ['firstName', 'lastName', 'phone']; // Kontakt
      case 1:
        return ['companyName', 'billingStreet', 'billingCity', 'billingZip', 'billingCountry']; // Rechnungsadresse
      case 2: // Geschäftsadresse
        const useBilling = this.onboardingForm?.get('useBillingAsBusiness')?.value;
        return useBilling
          ? []
          : ['businessStreet', 'businessCity', 'businessZip', 'businessCountry'];
      case 3:
        return this.isMember
          ? ['shopName', 'website'] // Dein Betrieb
          : ['shopName', 'website', 'suburl']; // Gutschein-Shop
      default:
        return [];
    }
  }

  isCurrentStepInvalid(touch: boolean = false): boolean {
    const fields = this.getStepFieldNames(this.currentStep);
    fields.forEach((key) => {
      const control = this.onboardingForm.get(key);
      if (touch) control?.markAsTouched();
    });
    return fields.some((key) => this.onboardingForm.get(key)?.invalid);
  }

  nextStep(): void {
    this.submitted = true;
    if (this.isCurrentStepInvalid(true)) return;

    this.loading = true;
    this.error = '';

    // Save current step data to backend
    this.saveCurrentStep().subscribe({
      next: () => {
        setTimeout(() => {
          // Brief delay to ensure animation is visible
          this.submitted = false;
          this.loading = false;

          // Mark current step fields as pristine after successful save
          this.markCurrentStepAsPristine();

          // Reset back navigation flag after successful forward navigation
          this.hasNavigatedBack = false;

          // Update completed steps count
          this.completedSteps = Math.max(this.completedSteps, this.currentStep + 1);

          if (this.currentStep < this.steps.length - 1) {
            this.currentStep++;
          }
        }, 300); // Reduced to 300ms
      },
      error: (error) => {
        this.handleStepError(error);
        this.loading = false;
      },
    });
  }

  previousStep(): void {
    if (this.currentStep > 0) {
      this.currentStep--;
      this.submitted = false;
      this.hasNavigatedBack = true; // Mark that user has navigated back
    }
  }

  isLastStep(): boolean {
    return this.currentStep === this.steps.length - 1;
  }

  isStepCompleted(stepIndex: number): boolean {
    return stepIndex < this.completedSteps;
  }

  getStepClasses(stepIndex: number): string {
    if (stepIndex < this.completedSteps) {
      // Completed step - green checkmark
      return 'bg-green-500 text-white border-green-500';
    } else if (stepIndex === this.currentStep) {
      // Current step - primary color
      return 'bg-(--primary-color) text-white border-(--primary-color)';
    } else {
      // Future step - gray
      return 'bg-gray-100 text-gray-500 border-gray-300';
    }
  }

  isStepClickable(stepIndex: number): boolean {
    // Step indicators are no longer clickable - users can only use Zurück button
    return false;
  }

  getStepTooltip(stepIndex: number): string {
    if (stepIndex < this.completedSteps) {
      return 'Abgeschlossener Schritt';
    } else if (stepIndex === this.currentStep) {
      return 'Aktueller Schritt';
    } else {
      return 'Schritt noch nicht verfügbar';
    }
  }

  onStepClick(stepIndex: number): void {
    // Step indicators are no longer clickable - this method is kept for template compatibility
    return;
  }

  /**
   * Save current step data to backend
   */
  saveCurrentStep(): Observable<any> {
    const formValue = this.onboardingForm.value;

    switch (this.currentStep) {
      case 0: // Step 1: Contact
        const step1Data: ProfileStep1Data = {
          first_name: formValue.firstName,
          last_name: formValue.lastName,
          phone: formValue.phone,
        };
        return this.authService.completeProfileStep1(step1Data);

      case 1: // Step 2: Billing Address
        const step2Data: ProfileStep2Data = {
          company_name: formValue.companyName,
          billing_street: formValue.billingStreet,
          billing_city: formValue.billingCity,
          billing_zip: formValue.billingZip,
          billing_country: formValue.billingCountry,
        };
        return this.authService.completeProfileStep2(step2Data);

      case 2: // Step 3: Business Address
        const step3Data: ProfileStep3Data = {
          use_billing_as_business: formValue.useBillingAsBusiness,
          business_street: formValue.businessStreet,
          business_city: formValue.businessCity,
          business_zip: formValue.businessZip,
          business_country: formValue.businessCountry,
        };
        return this.authService.completeProfileStep3(step3Data);

      default:
        // This shouldn't happen, but return empty observable as fallback
        return new Observable((subscriber) => subscriber.next({}));
    }
  }

  /**
   * Normalize website URL - add https:// if no protocol is provided
   */
  normalizeWebsiteUrl(websiteUrl: string): string | undefined {
    if (!websiteUrl) return undefined;
    if (!websiteUrl.match(/^https?:\/\//)) {
      return 'https://' + websiteUrl;
    }
    return websiteUrl;
  }

  /**
   * Handle step save errors
   */
  handleStepError(error: any): void {
    if (error.status === 400 && error.error?.detail?.errors) {
      // Process field-specific errors
      const fieldErrors = error.error.detail.errors;

      // Set field-specific errors on form controls
      fieldErrors.forEach((fieldError: { field: string; message: string }) => {
        const control = this.onboardingForm.get(this.mapFieldName(fieldError.field));
        if (control) {
          control.setErrors({ serverError: fieldError.message });
          control.markAsTouched();
        }
      });

      this.error = 'Bitte korrigiere die markierten Felder und versuche es erneut.';
    } else {
      this.error = error.error?.detail || 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.';
    }
  }

  /**
   * Gets the storefront base domain for display in the onboarding form
   */
  getStorefrontBaseDomain(): string {
    const baseDomain = this.storefrontService.getStorefrontBaseDomain();

    // For development, show a simplified version
    if (baseDomain === 'localhost') {
      return 'localhost:3000';
    }

    return baseDomain;
  }

  onSubmit() {
    this.submitted = true;
    this.error = '';

    // Validate current step first
    if (this.isCurrentStepInvalid(true)) {
      return; // Don't proceed if validation fails
    }

    // If there are unsaved changes, save current step first
    if (this.hasUnsavedChanges()) {
      this.loading = true;
      this.saveCurrentStep().subscribe({
        next: () => {
          this.loading = false;
          this.submitted = false;

          // Mark current step fields as pristine after successful save
          this.markCurrentStepAsPristine();

          // Reset back navigation flag after successful save
          this.hasNavigatedBack = false;

          // After saving, proceed with normal flow
          if (this.isLastStep()) {
            // Complete the profile
            this.completeProfile();
          } else {
            // Move to next step
            this.nextStep();
          }
        },
        error: (error) => {
          this.handleStepError(error);
          this.loading = false;
        },
      });
      return;
    }

    // Submit only on last step; otherwise, move forward
    if (!this.isLastStep()) {
      this.nextStep();
      return;
    }

    if (this.onboardingForm.invalid) {
      return;
    }

    this.completeProfile();
  }

  /**
   * Complete the profile (final step)
   */
  completeProfile(): void {
    // Safety check: validate before completing profile
    if (this.isCurrentStepInvalid(true)) {
      this.submitted = true;
      return;
    }

    this.loading = true;

    // Final step: a shop gets its storefront (and Stripe account), a member its name.
    const value = this.onboardingForm.value;
    const finalStep = this.isMember
      ? this.authService.completeMemberProfile({
          name: value.shopName,
          website: this.normalizeWebsiteUrl(value.website),
        })
      : this.authService.completeProfileStep4({
          name: value.shopName,
          website: this.normalizeWebsiteUrl(value.website),
          suburl: value.suburl,
        } as ProfileStep4Data);

    finalStep.subscribe({
      next: () => {
        // Update completed steps count for final step
        this.completedSteps = 4;

        this.logger.log('🎉 [OnboardingComponent] Step 4 completed, refreshing company data...');

        // Refresh company data in token storage before redirecting
        this.authService.refreshCompanyData().subscribe({
          next: (companyData) => {
            this.logger.log('✅ [OnboardingComponent] Company data refreshed successfully');

            // Manually trigger CompanyService to reload data after profile completion
            // This ensures the CompanyService BehaviorSubject is updated with fresh data
            const companyId = companyData.public_id;
            if (companyId) {
              this.logger.log('🔄 [OnboardingComponent] Triggering CompanyService to reload data');

              // Reload company data in CompanyService to ensure the observable emits
              this.companyService
                .reloadCompanyData()
                .then(() => {
                  this.logger.log(
                    '✅ [OnboardingComponent] CompanyService data reloaded, navigating to dashboard',
                  );
                  setTimeout(() => {
                    this.router.navigate(['/']);
                  }, 300);
                })
                .catch((error) => {
                  this.logger.error(
                    '❌ [OnboardingComponent] Error reloading company data:',
                    error,
                  );
                  // Still navigate even if reload fails
                  setTimeout(() => {
                    this.router.navigate(['/']);
                  }, 300);
                });
            } else {
              setTimeout(() => {
                this.router.navigate(['/']);
              }, 300);
            }
          },
          error: (error) => {
            this.logger.error('❌ [OnboardingComponent] Error refreshing company data:', error);
            // Fallback: still redirect but log the issue
            setTimeout(() => {
              this.router.navigate(['/']);
            }, 300);
          },
        });
      },
      error: (error) => {
        this.handleStepError(error);
        this.loading = false;
      },
    });
  }

  /**
   * Maps API field names to form control names
   */
  private mapFieldName(apiFieldName: string): string {
    const fieldMapping: { [key: string]: string } = {
      first_name: 'firstName',
      last_name: 'lastName',
      name: 'shopName',
      company_name: 'companyName',
      billing_street: 'billingStreet',
      billing_city: 'billingCity',
      billing_zip: 'billingZip',
      billing_country: 'billingCountry',
      business_street: 'businessStreet',
      business_city: 'businessCity',
      business_zip: 'businessZip',
      business_country: 'businessCountry',
      phone: 'phone',
      website: 'website',
      suburl: 'suburl',
    };

    return fieldMapping[apiFieldName] || apiFieldName;
  }
}
